import TIMEZONES from '../../../data/timezones';
/**
 * Formatter for EST timezone dates
 * Returns dates in format MM/DD/YYYY
 */
const estFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONES.NEW_YORK, // EST timezone
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

/**
 * Gets the current date formatted in EST timezone
 * @returns {string} Current date in format MM/DD/YYYY in EST timezone
 */
const getEasternOffset = () => {
    // Create a date in Eastern time
    const easternDate = new Date().toLocaleString('en-US', {
        timeZone: TIMEZONES.NEW_YORK,
        timeZoneName: 'short',
    });

    // Get the offset from UTC
    const easternTime = new Date(easternDate);
    return easternTime.getTimezoneOffset() / -60;
};

/**
 * Gets the hour difference between local time and EST
 * @returns {number} Hour difference between local time and EST
 */
const getHourDifference = () => {
    // Get the EST offset from UTC (-5 hours)
    const estOffset = getEasternOffset();

    // Get the local timezone offset from UTC (e.g., PST would be -8)
    // getTimezoneOffset() returns minutes, so divide by -60 to get hours
    const localOffset = new Date().getTimezoneOffset() / -60;

    // Calculate the hour difference between local time and EST
    // Example: If user is in PST (-8), and EST is (-5):
    // hourDiff = -8 - (-5) = -3 hours
    return localOffset - estOffset;
};

/**
 * Gets the current date/time in EST timezone
 * Handles daylight savings and timezone conversions
 * @returns {Date} Current date/time in EST
 */
const getCurrentEstDate = () => {
    const now = new Date();
    const estOffset = -5; // EST UTC offset
    const localOffset = now.getTimezoneOffset() / -60; // Local UTC offset
    const hourDiff = localOffset - estOffset;

    // Create new date adjusted to EST
    const estDate = new Date(now.getTime() - hourDiff * 60 * 60 * 1000);
    return estDate;
};

/**
 * Checks if call center is open for a given date
 * @param {Date} date - Date to check
 * @param {Object} callbackData - Object containing call center hours
 * @param {number} callbackData.cc_end_hour - Hour that call center closes (24h format)
 * @returns {boolean} True if call center is open, false if closed
 */
const isCallCenterOpen = (date, callbackData) => {
    // Return false if no date provided
    if (!date) {
        return false;
    }

    const currentDate = new Date();
    const selectedDate = new Date(date);
    const selectedHour = selectedDate.getHours();

    // If it's today, check if call center is closed
    const isToday =
        selectedDate.getDate() === currentDate.getDate() &&
        selectedDate.getMonth() === currentDate.getMonth() &&
        selectedDate.getFullYear() === currentDate.getFullYear();

    if (isToday) {
        // Check if current time is past call center closing time
        if (selectedHour >= callbackData.cc_end_hour) {
            return false;
        }
    }

    return true;
};

/**
 * Resets time portion of date to midnight if date is current
 * @param {Date} date - Date to check and potentially reset
 * @returns {Date} Date with time reset to midnight if current, otherwise unchanged
 */
const resetTimeIfCurrent = date => {
    if (!date) return date;

    const inputDate = new Date(date);
    const now = new Date();

    // Compare dates in UTC to avoid timezone issues
    const isToday =
        inputDate.getUTCFullYear() === now.getUTCFullYear() &&
        inputDate.getUTCMonth() === now.getUTCMonth() &&
        inputDate.getUTCDate() === now.getUTCDate();

    if (isToday) {
        inputDate.setUTCHours(0, 0, 0, 0);
    }

    return inputDate;
};

/**
 * Gets the next available date when call center is open
 * Handles weekends and after-hours scenarios
 * @param {Date} date - Starting date to check from
 * @param {number} ccEndHour - Hour that call center closes (24h format)
 * @returns {Date} Next available date when call center is open
 */
const getNextOpenDate = (date, { cc_end_hour, cc_saturday_end_hour, cc_start_hour, cc_saturday_start_hour }) => {
    const dayOfWeekEST = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const ccEndHour = dayOfWeekEST === 6 ? cc_saturday_end_hour : cc_end_hour;
    const ccStartHour = dayOfWeekEST === 6 ? cc_saturday_start_hour : cc_start_hour;
    // Create a new date object to avoid modifying the input
    const resultDate = new Date(date);
    const currentHour = resultDate.getHours();
    const currentMinutes = resultDate.getMinutes();

    // If it's Sunday (0), move to next Monday
    if (dayOfWeekEST === 0) {
        resultDate.setDate(resultDate.getDate() + 1);
        resultDate.setUTCHours(5, 0, 0, 0); // Set to midnight EST (5 AM UTC)
        return resultDate;
    }

    // If it's not Sunday, check if we're past cutoff time (end hour minus 30 minutes)
    const cutoffHour = Math.floor(ccEndHour - 0.5); // 16 for 5 PM closing
    const cutoffMinute = ((ccEndHour - 0.5) % 1) * 60; // 30 minutes

    // Check if we're past the cutoff time
    if (currentHour > cutoffHour || (currentHour === cutoffHour && currentMinutes > cutoffMinute)) {
        // Move to next day
        resultDate.setDate(resultDate.getDate() + 1);
        resultDate.setUTCHours(5, 0, 0, 0); // Set to midnight EST (5 AM UTC)

        // If next day would be Sunday, move to Monday
        const nextDay = new Date(resultDate);
        if (nextDay.getDay() === 0) {
            resultDate.setDate(resultDate.getDate() + 1);
            resultDate.setHours(ccStartHour, 0, 0, 0);
        }
    }

    return resultDate;
};

/**
 * Checks if call center is done for the day
 * @param {Date} date - Date to check
 * @param {Object} schedulerData - Scheduler data object
 * @param {number} schedulerData.cc_end_hour - Call center closing hour (24h format)
 * @param {number} schedulerData.cc_saturday_end_hour - Call center closing hour on Saturday (24h format)
 * @returns {boolean} True if call center is done for the day, false otherwise
 */
const isCcDoneForTheDay = (date, { cc_end_hour, cc_saturday_end_hour }) => {
    const dayOfWeekEST = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const ccEndHour = dayOfWeekEST === 6 ? cc_saturday_end_hour : cc_end_hour;
    const currentHour = date.getHours();
    return currentHour >= ccEndHour;
};

/**
 * Converts an hour number to a Date object with that hour set
 * @param {number} hourNumber - Hour in 24-hour format
 * @returns {Date} Date object set to the specified hour with minutes at 30
 */
const hourToDate = hourNumber => {
    return new Date(new Date().setHours(hourNumber - 1, 30));
};

/**
 * Processes the minimum time by converting to local time and adjusting to 8 AM if necessary
 * @param {Date} date - Date to process
 * @param {Object} schedulerData - Scheduler data object
 * @returns {Date} Processed date adjusted to 8 AM if necessary
 */
function processMinTime(date, schedulerData) {
    const min = generateMinTime(date, schedulerData);
    const adjustedTime = minEightAm(min);

    return adjustedTime;
}

/**
 * Adjusts a date to 8 AM if it's before 8 AM
 * @param {Date} date - Date to adjust
 * @returns {Date} Date adjusted to 8 AM if it's before 8 AM, otherwise the original date
 */
function minEightAm(date) {
    const dateTime = new Date(date);
    const hours = dateTime.getHours();

    if (hours < 8) {
        dateTime.setHours(8, 0, 0, 0);
    }

    return dateTime;
}

/**
 * Generates the minimum selectable time based on current EST time
 * @param {Date|null} selectedDate - The currently selected date
 * @param {Object} schedulerData - Configuration object
 * @param {number} schedulerData.cc_start_hour - Call center opening hour (24h format)
 * @param {number} schedulerData.cc_saturday_start_hour - Call center opening hour on Saturday
 * @returns {Date} Minimum selectable time in EST timezone
 */
const generateMinTime = (selectedDate, schedulerData) => {
    if (!selectedDate) {
        return hourToDate(0);
    }

    const today = new Date();

    const isToday =
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getDate() === today.getDate();

    const minHour = selectedDate.getDay() === 6 ? schedulerData.cc_saturday_start_hour : schedulerData.cc_start_hour;

    if (isToday) {
        const currentHour = today.getHours();
        const currentMinutes = today.getMinutes();
        // If current hour is before opening, return opening time
        if (currentHour < minHour) {
            return hourToDate(minHour);
        }

        // Return current EST time rounded up to next 30-minute interval
        const roundedMinutes = currentMinutes <= 30 ? 30 : 0;
        const roundedHour = currentMinutes <= 30 ? currentHour : currentHour + 1;

        // Create date in EST timezone
        return new Date(today.setHours(roundedHour, roundedMinutes, 0, 0));
    }

    // For future dates, return normal opening time
    return hourToDate(minHour);
};

/**
 * Generates the maximum selectable time
 * @param {Date|null} selectedDate - The currently selected date
 * @param {Object} schedulerData - Configuration object
 * @param {number} schedulerData.cc_end_hour - Call center closing hour (24h format)
 * @param {number} schedulerData.cc_saturday_end_hour - Call center closing hour on Saturday (24h format)
 * @returns {Date} Maximum selectable time in EST timezone
 */
const generateMaxTime = (selectedDate, schedulerData) => {
    if (!selectedDate) {
        return hourToDate(0);
    }
    if (selectedDate.getDay() === 6) {
        return hourToDate(schedulerData.cc_saturday_end_hour);
    }

    return hourToDate(schedulerData.cc_end_hour);
};

/**
 * Converts a date from EST to local timezone
 * @param {Date} estDate - Date object in EST timezone
 * @returns {Date} Date object converted to local timezone
 */
const convertEstToLocal = estDate => {
    if (!estDate) {
        return null;
    }

    // Get EST offset (-5 or -4 depending on DST)
    const estOffset =
        new Date().toLocaleString('en-US', { timeZone: TIMEZONES.NEW_YORK }).match(/GMT([+-]\d+)/)?.[1] / 100 || -5;

    // Get local timezone offset in hours
    const localOffset = new Date().getTimezoneOffset() / -60;

    // Calculate difference between local and EST
    const hourDiff = localOffset - estOffset;

    // Create new date adjusted to local time
    return new Date(estDate.getTime() + hourDiff * 60 * 60 * 1000);
};

/**
 * Checks if a given event is a datepicker day click
 * @param {Object} event - Event object
 * @returns {boolean} True if event is a datepicker day click, false otherwise
 */
const userClickedADateElement = event => {
    return Boolean(event?.target?.classList?.contains('react-datepicker__day'));
};

/**
 * Checks if a given time falls within call center hours
 * @param {Date} time - Time to check
 * @param {number} ccStartHour - Call center opening hour (24h format)
 * @param {number} ccEndHour - Call center closing hour (24h format)
 * @returns {boolean} True if time is within call center hours
 */
const isWithinCallCenterHours = (time, ccStartHour, ccEndHour) => {
    if (!time) return false;

    // Convert the time to EST for comparison
    const estDate = new Date(time.toLocaleString('en-US', { timeZone: TIMEZONES.NEW_YORK }));
    const estHour = estDate.getHours();

    // Check if hour is within range (inclusive of start, exclusive of end)
    return estHour >= ccStartHour && estHour < ccEndHour;
};

/**
 * Calculates the number of days available to schedule
 * @param {Date} date - Date to check
 * @param {Object} schedulerData - Scheduler data object
 * @returns {number} Number of days available to schedule
 */
const numberOfDaysAvailableToSchedule = (date, schedulerData) => {
    const weAreDoneForTheDay = isCcDoneForTheDay(date, schedulerData);
    const endOffset = (schedulerData?.schedule_time || 0) + (weAreDoneForTheDay ? 1 : 0);
    const maxDate = new Date(Date.now() + endOffset * 24 * 60 * 60 * 1000);
    const wouldBeSunday = maxDate.getDay() === 0;
    if (maxDate.getDay() === 0) {
        return endOffset + 1;
    }
    return endOffset;
};

/**
 * Gets the maximum date available to schedule
 * @param {Date} date - Date to check
 * @param {Object} schedulerData - Scheduler data object
 * @returns {Date} Maximum date available to schedule
 */
const getMaxDate = (date, schedulerData) => {
    const endOffset = numberOfDaysAvailableToSchedule(date, schedulerData);
    const maxDate = new Date(Date.now() + endOffset * 24 * 60 * 60 * 1000);
    return maxDate;
};

/**
 * Validates if a given date is available for scheduling callbacks
 * @param {Date} date - The date to check
 * @param {boolean} weAreDoneForTheDay - Whether the call center is closed for current day
 * @param {Object} schedulerData - Configuration object containing call center hours
 * @returns {boolean} - Returns false if date is invalid for scheduling, true if available
 */
const dateValidator = (date, weAreDoneForTheDay, schedulerData) => {
    // Check if the date being validated is today
    const dateToValidateToday = date.getDate() === new Date().getDate();

    // If call center is done for today, disable today's date
    if (weAreDoneForTheDay && dateToValidateToday) {
        return false;
    }

    // Check if date falls within call center operating hours
    if (!isCallCenterOpen(date, schedulerData)) {
        return false;
    }

    // Exclude Sundays (0 = Sunday in getDay())
    return date.getDay() !== 0;
};

/**
 * Get the browser timezone in long or short format
 * @param {boolean} longFormat - If true, returns long format, otherwise short
 * @returns {string} - The timezone string
 */
const getBrowserTimezone = longFormat => {
    const options = {
        timeZoneName: longFormat ? 'long' : 'short',
    };
    return new Intl.DateTimeFormat('en-US', options)
        .formatToParts(new Date())
        .find(part => part.type === 'timeZoneName').value;
};

/**
 * Get the call center times in the user's local timezone
 * @param {Date} selectedDate - The date to check
 * @param {Object} schedulerData - Configuration object containing call center hours
 * @returns {Array} - Array of Date objects representing available times in blocks of 30 minutes
 */
const getIncludedTimes = (selectedDate, schedulerData) => {
    const intervals = [];
    const stepMinutes = 30;
    const baseDate = selectedDate ? new Date(selectedDate) : new Date();
    const isSaturday = baseDate.getDay() === 6;

    const base = new Date(baseDate);

    // Get minTime and maxTime from schedulerData
    let minTime = new Date(
        base.setHours(isSaturday ? schedulerData.cc_saturday_start_hour : schedulerData.cc_start_hour, 0, 0, 0)
    );

    let maxTime = new Date(
        base.setHours(isSaturday ? schedulerData.cc_saturday_end_hour : schedulerData.cc_end_hour, 0, 0, 0)
    );

    const currentRealTime = new Date(); // Current real-world time
    const isToday = baseDate.toDateString() === currentRealTime.toDateString();
    const now = new Date(baseDate);

    if (isToday) {
        // If the selected date is today, "now" should be the current time
        now.setHours(
            currentRealTime.getHours(),
            currentRealTime.getMinutes(),
            currentRealTime.getSeconds(),
            currentRealTime.getMilliseconds()
        );
    } else {
        // If the selected date is in the future, "now" should be reset to midnight
        now.setHours(0, 0, 0, 0);
    }

    // Check if two blocks of time are needed because of the timezone difference
    const needsTwoBlocks = minTime > maxTime;

    if (needsTwoBlocks) {
        // Block 1: From 00:00 to maxTime
        const targetDate = new Date(baseDate);
        targetDate.setHours(0, 0, 0, 0); // Reset to midnight
        let currentTime = new Date(targetDate); // Start at 00:00

        while (currentTime < maxTime) {
            // Only now and future times are valid
            if (currentTime >= now) {
                intervals.push(new Date(currentTime));
            }

            currentTime.setMinutes(currentTime.getMinutes() + stepMinutes);

            // Stop condition: No times allowed after maxTime
            if (currentTime >= maxTime) {
                break;
            }
        }

        // Block 2: From minTime to 23:59:59
        currentTime = new Date(minTime); // Start at minTime
        const endOfDay = new Date(baseDate);
        endOfDay.setHours(23, 59, 59, 999); // Set to the end of the day

        while (currentTime <= endOfDay) {
            if (currentTime >= now) {
                intervals.push(new Date(currentTime));
            }

            currentTime.setMinutes(currentTime.getMinutes() + stepMinutes);

            // Stop condition: no times allowed after endOfDay
            if (currentTime > endOfDay) {
                break;
            }
        }
    } else {
        // One block: From minTime to maxTime, if timezone gaps are not present.
        let currentTime = new Date(minTime); // Start at minTime
        while (currentTime < maxTime) {
            if (currentTime >= now) {
                intervals.push(new Date(currentTime));
            }

            currentTime.setMinutes(currentTime.getMinutes() + stepMinutes);

            // Stop condition: Ensure we don't go beyond maxTime
            if (currentTime >= maxTime) {
                break;
            }
        }
    }

    return intervals;
};

export {
    convertEstToLocal,
    dateValidator,
    estFormatter,
    generateMaxTime,
    generateMinTime,
    getBrowserTimezone,
    getCurrentEstDate,
    getEasternOffset,
    getHourDifference,
    getIncludedTimes,
    getMaxDate,
    getNextOpenDate,
    hourToDate,
    isCallCenterOpen,
    isCcDoneForTheDay,
    isWithinCallCenterHours,
    numberOfDaysAvailableToSchedule,
    processMinTime,
    resetTimeIfCurrent,
    userClickedADateElement,
};
